import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip } from '@mui/material';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import DynamicTable from '../../components/DynamicTable/DynamicTable';
import { employerApi } from '../../services/apiClient';
import { useCompanyContext } from '../../context/CompanyContext';

interface EmployerInvoice {
  id: string;
  companyID: string;
  createdAt: string;
  status: string;
  invoiceNumber: string;
  finalTotal: number;
  totalProductValue: number;
  totalFees: number;
  sent: boolean;
  xeroInvoiceNumber?: string;
  xeroInvoiceID?: string;
  publicUrl?: string | null;
}

const Invoicing = () => {
  const { activeCompany } = useCompanyContext();
  const [invoices, setInvoices] = useState<EmployerInvoice[]>([]);
  const [fetchingUrl, setFetchingUrl] = useState<string | null>(null);

  const companyID = activeCompany?.companyID;

  const {
    data: invoicesResponse,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['invoicing', companyID],
    queryFn: async () => {
      const response = await employerApi.get('/invoicing');
      return response;
    },
    enabled: Boolean(companyID),
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Update invoices when response changes
  useEffect(() => {
    if (invoicesResponse) {
      const data = invoicesResponse?.data?.data || invoicesResponse?.data || [];
      setInvoices(Array.isArray(data) ? data : []);
    } else if (!loading) {
      // Only clear if we're not loading (to avoid clearing during refetch)
      setInvoices([]);
    }
  }, [invoicesResponse, loading]);

  const rows = invoices.map((inv) => {
    console.log(inv);
    // const created = inv.createdAt ? DateTime.fromISO(inv.createdAt) : null;
    return [

      inv.invoiceNumber,
      inv.xeroInvoiceNumber || '',
      inv.createdAt,

      `£${(inv.finalTotal ?? 0).toFixed(2)}`,
      // <Chip
      //   label={inv.status}
      //   size="small"
      //   color={inv.status === 'CREATED' || inv.status === 'AUTHORISED' ? 'primary' : inv.status === 'PAID' ? 'success' : 'default'}
      //   variant="outlined"
      // />,
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleRowClick(inv.invoiceNumber);
        }}
        disabled={fetchingUrl === inv.invoiceNumber}
        className="flex flex-row gap-2 items-center justify-center px-3 py-1 rounded-md border border-gray-300 bg-white text-xs hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {fetchingUrl === inv.invoiceNumber ? (
          <>
            <CircularProgress size={12} />
            <span>Loading...</span>
          </>
        ) : (
          <>
            <img src={require('../../assets/openInNewTab.png')} className="w-4 h-4" />
            <span>{inv.publicUrl ? 'View Invoice' : 'Fetch & View'}</span>
          </>
        )}
      </button>,
    ];
  });

  const handleRowClick = async (invoiceNumber: string) => {
    const selected = invoices.find((inv) => inv.invoiceNumber === invoiceNumber);
    if (!selected) return;

    // If we already have a public URL, open it
    if (selected.publicUrl) {
      window.open(selected.publicUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Otherwise, fetch it from the backend
    try {
      setFetchingUrl(invoiceNumber);

      // employerApi.post() returns response.data directly
      const response = await employerApi.post<{ success: boolean; publicUrl?: string; message?: string }>(
        `/invoicing/${invoiceNumber}/fetch-url`
      );

      // Log response for debugging
      console.log('Fetch URL response:', response);

      // Check if response is valid and has the URL
      if (response && typeof response === 'object' && response.success === true && response.publicUrl) {
        // Update the invoice in state with the new URL
        setInvoices(prev => prev.map(inv =>
          inv.invoiceNumber === invoiceNumber
            ? { ...inv, publicUrl: response.publicUrl! }
            : inv
        ));

        // Open the URL
        window.open(response.publicUrl, '_blank', 'noopener,noreferrer');
      } else {
        // Response structure doesn't match expected format
        console.error('Invalid response structure:', {
          response,
          hasSuccess: response?.success,
          hasPublicUrl: !!response?.publicUrl,
          successValue: response?.success,
          publicUrlValue: response?.publicUrl
        });
        const errorMessage = response?.message || 'Failed to fetch invoice URL. Invalid response from server.';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error fetching invoice URL:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch invoice URL. Please try again.';
      toast.error(errorMessage);
    } finally {
      setFetchingUrl(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Invoices
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, fontWeight: 400 }}>
        View and manage invoices for your company. Any new invoices will appear here when they are issued.
      </Typography>
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            tableId="EmployerInvoicesTable"
            cols={['Invoice #', 'Xero Inv #', 'Date', 'Total', 'Actions']}
            filterableColumns={['Status']}
            showExport={true}
            rows={rows}
            loading={loading}
            idIndexInColumns={0}
            showID={false}
            onRowClicked={() => { }} // Handled by button click instead
          />
        )}
      </Paper>
    </Box>
  );
};

export default Invoicing;

